import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../auth/useAuth.js';
import { CloseIcon, MicIcon, SparkleIcon } from '../components/icons.jsx';

const FIELD =
  'w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';
const LABEL = 'block text-sm font-semibold text-gray-700';
const HINT = 'mt-1 text-xs text-gray-500';

const LIMITS = {
  idea: 2000,
  moral: 500,
  protoKind: 80,
  protoName: 80,
  protoTraits: 500,
  protoPersonality: 500,
};

export default function CreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInvite, logout } = useAuth();
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(isInvite ? 'checking' : 'ok');

  // Si llegamos desde el dictado por voz, el state trae los campos extraidos,
  // los costes asociados (STT + extraccion) y el transcript para mostrarlo.
  const [form, setForm] = useState(() => buildInitialForm(location.state?.prefill));
  const [voiceCosts, setVoiceCosts] = useState(() => location.state?.voiceCosts ?? null);
  const [transcript, setTranscript] = useState(() => location.state?.transcript ?? null);
  const [transcriptDismissed, setTranscriptDismissed] = useState(false);

  // Si la pagina se vuelve a navegar con un nuevo prefill (dictado desde aqui)
  // refrescamos los campos. Limpiamos location.state al consumirlo para que un
  // refresh del navegador no vuelva a forzar el prefill.
  useEffect(() => {
    if (location.state?.prefill) {
      setForm(buildInitialForm(location.state.prefill));
      setVoiceCosts(location.state.voiceCosts ?? null);
      setTranscript(location.state.transcript ?? null);
      setTranscriptDismissed(false);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    api.stories.options().then(setOptions).catch((e) => setError(e.message));
  }, []);

  // Para sesiones de invitacion validamos al entrar a /create que el codigo
  // siga vigente. Si ya se uso (cuento ready) o ya hay un draft en curso para
  // ese mismo codigo, mostramos un mensaje claro en vez de dejar que el draft
  // falle a mitad.
  useEffect(() => {
    if (!isInvite) return;
    let cancelled = false;
    setInviteStatus('checking');
    api.auth.me().then((data) => {
      if (cancelled) return;
      if (data.role !== 'invite') {
        setInviteStatus('ok');
      } else if (data.canCreate) {
        setInviteStatus('ok');
      } else if (data.reservedStoryId) {
        setInviteStatus('in_use');
      } else {
        setInviteStatus('consumed');
      }
    }).catch(() => {
      if (!cancelled) setInviteStatus('consumed');
    });
    return () => { cancelled = true; };
  }, [isInvite]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateProto = (patch) =>
    setForm((f) => ({ ...f, protagonist: { ...f.protagonist, ...patch } }));

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    if (form.idea.trim().length < 3) {
      setError('Describe brevemente la idea del cuento.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        idea: form.idea.trim(),
        languageCode: form.languageCode,
        styleId: form.styleId,
        ageRange: form.ageRange,
        endingType: form.endingType,
        moral: form.moral.trim() || undefined,
        numScenes: Number(form.numScenes),
        protagonist: cleanProtagonist(form.protagonist),
        ...(voiceCosts && voiceCosts.length > 0 ? { __voiceCosts: voiceCosts } : {}),
      };
      const story = await api.stories.draft(payload);
      navigate(`/draft/${story.id}`, { state: { initial: story } });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (isInvite && inviteStatus === 'checking') {
    return <p className="mx-auto max-w-3xl px-4 py-10 text-gray-500">Validando tu codigo de invitacion...</p>;
  }

  if (isInvite && inviteStatus !== 'ok') {
    const isConsumed = inviteStatus === 'consumed';
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-brand-700">
          {isConsumed ? 'Codigo ya utilizado' : 'Tienes un cuento en curso'}
        </h1>
        <p className="mt-3 text-gray-700">
          {isConsumed
            ? 'Tu codigo de invitacion permite crear un solo cuento, y ya lo has usado. Ve a la galeria para verlo.'
            : 'Tu codigo de invitacion ya esta vinculado a un cuento en proceso de generacion. Cuando termine podras consultarlo en la galeria.'}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Ir a la galeria
          </Link>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cerrar sesion
          </button>
        </div>
      </div>
    );
  }

  if (!options) {
    return <p className="mx-auto max-w-3xl px-4 py-10 text-gray-500">Cargando opciones...</p>;
  }

  const sceneOptions = [{ value: 0, label: 'Aleatorio' }];
  for (let n = options.sceneCounts.min; n <= options.sceneCounts.max; n++) {
    sceneOptions.push({ value: n, label: `${n} escenas` });
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-3xl font-bold text-brand-700">Crear un cuento nuevo</h1>
      <p className="mt-1 text-gray-600">
        Rellena lo que sepas. Los campos opcionales los inventa la IA por ti.
      </p>

      {transcript && !transcriptDismissed && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-purple-200 bg-purple-50 p-4 text-purple-900">
          <MicIcon size="1.2em" className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
              Campos rellenados desde tu dictado
            </div>
            <p className="mt-1 text-sm italic">"{transcript}"</p>
            <p className="mt-2 text-xs text-purple-700">
              Revisa los campos y ajusta lo que quieras antes de generar.
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar aviso"
            onClick={() => setTranscriptDismissed(true)}
            className="rounded-full p-1 text-purple-500 hover:bg-purple-100 hover:text-purple-800"
          >
            <CloseIcon size="1.1em" />
          </button>
        </div>
      )}

      {error && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
      )}

      <form className="mt-8 space-y-6" onSubmit={onSubmit}>
        {/* Idea */}
        <div>
          <label className={LABEL}>Idea central del cuento *</label>
          <textarea
            rows={4}
            maxLength={LIMITS.idea}
            className={FIELD}
            placeholder="Ej: Un dragoncito timido cuyas llamas son de colores diferentes. Vive en un valle con conejos azules y aprende que sus llamas pueden curar plantas marchitas..."
            value={form.idea}
            onChange={(e) => update({ idea: e.target.value })}
          />
          <CharCount value={form.idea} max={LIMITS.idea} />
        </div>

        {/* Estilo: selector visual */}
        <div>
          <label className={LABEL}>Estilo de ilustracion</label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {options.styles.map((s) => {
              const active = form.styleId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => update({ styleId: s.id })}
                  className={`rounded-xl border p-3 text-left transition ${
                    active
                      ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                      : 'border-gray-200 bg-white hover:border-brand-200'
                  }`}
                >
                  <div className="font-semibold text-brand-700">{s.label}</div>
                  <div className="mt-1 text-xs text-gray-500">{s.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Idioma / edad / final / escenas */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={LABEL}>Idioma / dialecto</label>
            <select
              className={FIELD}
              value={form.languageCode}
              onChange={(e) => update({ languageCode: e.target.value })}
            >
              {options.languages.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL}>Edad objetivo</label>
            <select
              className={FIELD}
              value={form.ageRange}
              onChange={(e) => update({ ageRange: e.target.value })}
            >
              {options.ageRanges.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL}>Tipo de final</label>
            <select
              className={FIELD}
              value={form.endingType}
              onChange={(e) => update({ endingType: e.target.value })}
            >
              {options.endings.map((x) => (
                <option key={x.id} value={x.id}>{x.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL}>Numero de escenas</label>
            <select
              className={FIELD}
              value={form.numScenes}
              onChange={(e) => update({ numScenes: Number(e.target.value) })}
            >
              {sceneOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Moraleja */}
        <div>
          <label className={LABEL}>Moraleja o tema (opcional)</label>
          <input
            className={FIELD}
            maxLength={LIMITS.moral}
            placeholder="Ej: la amistad es mas fuerte que el miedo"
            value={form.moral}
            onChange={(e) => update({ moral: e.target.value })}
          />
          <p className={HINT}>Si lo dejas vacio, la IA elegira un tema apropiado.</p>
        </div>

        {/* Protagonista */}
        <fieldset className="rounded-xl border border-gray-200 bg-white p-4">
          <legend className="px-2 text-sm font-semibold text-brand-700">Protagonista (opcional)</legend>
          <p className="text-xs text-gray-500">Los campos vacios los inventa la IA.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Tipo / especie</label>
              <input
                className={FIELD}
                maxLength={LIMITS.protoKind}
                placeholder="dragoncito, conejita, niña..."
                value={form.protagonist.kind}
                onChange={(e) => updateProto({ kind: e.target.value })}
              />
            </div>
            <div>
              <label className={LABEL}>Nombre</label>
              <input
                className={FIELD}
                maxLength={LIMITS.protoName}
                placeholder="Pira, Luna..."
                value={form.protagonist.name}
                onChange={(e) => updateProto({ name: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL}>Rasgos fisicos</label>
              <input
                className={FIELD}
                maxLength={LIMITS.protoTraits}
                placeholder="escamas turquesa, alas doradas..."
                value={form.protagonist.traits}
                onChange={(e) => updateProto({ traits: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL}>Personalidad</label>
              <input
                className={FIELD}
                maxLength={LIMITS.protoPersonality}
                placeholder="curioso, valiente, timido..."
                value={form.protagonist.personality}
                onChange={(e) => updateProto({ personality: e.target.value })}
              />
            </div>
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-3 text-lg font-semibold text-white shadow-lg hover:bg-brand-700 disabled:opacity-50"
        >
          <SparkleIcon size="1.2em" />
          {submitting ? 'Creando boceto...' : 'Generar boceto del cuento'}
        </button>
        <p className="text-center text-xs text-gray-500">
          El boceto tarda unos 8 segundos. Luego podras revisarlo antes de generar las ilustraciones.
        </p>
      </form>
    </div>
  );
}

function cleanProtagonist(p) {
  const out = {};
  for (const k of ['kind', 'name', 'traits', 'personality']) {
    const v = p[k]?.trim();
    if (v) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

const DEFAULT_FORM = {
  idea: '',
  languageCode: 'es-neutro',
  styleId: 'random',
  ageRange: '6-9',
  endingType: 'random',
  moral: '',
  // 0 = aleatorio (el backend resuelve el numero concreto al crear el draft).
  numScenes: 0,
  protagonist: { kind: '', name: '', traits: '', personality: '' },
};

/**
 * Mezcla los campos extraidos del dictado con los valores por defecto del
 * formulario para que cualquier campo no rellenado por la IA tenga un valor
 * coherente.
 */
function buildInitialForm(prefill) {
  if (!prefill) return DEFAULT_FORM;
  const { coloring: _ignoredColoring, ...rest } = prefill;
  return {
    ...DEFAULT_FORM,
    ...rest,
    protagonist: {
      ...DEFAULT_FORM.protagonist,
      ...(prefill.protagonist ?? {}),
    },
    // moral viene como string del extractor, garantizamos string aqui.
    moral: prefill.moral ?? '',
  };
}

function CharCount({ value, max }) {
  const n = (value ?? '').length;
  const remaining = max - n;
  const warn = remaining <= Math.min(100, Math.floor(max * 0.1));
  return (
    <p className={`mt-1 text-right text-xs ${warn ? 'text-amber-600' : 'text-gray-400'}`}>
      {n} / {max}
    </p>
  );
}
